/**
 * React Query hooks for snapshot operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useSnapshots
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { snapshotService } from '@/services';

/**
 * Query key factory for snapshots.
 * Ensures consistent cache key generation.
 */
export const snapshotKeys = {
  all: ['snapshots'] as const,
  lists: () => [...snapshotKeys.all, 'list'] as const,
  list: () => [...snapshotKeys.lists()] as const,
  details: () => [...snapshotKeys.all, 'detail'] as const,
  detail: (id: string) => [...snapshotKeys.details(), id] as const,
};

/**
 * Hook to fetch total count of snapshots
 *
 * @example
 * const { data: count } = useSnapshotsCount();
 */
export function useSnapshotsCount() {
  return useQuery({
    queryKey: [...snapshotKeys.all, 'count'],
    queryFn: () => snapshotService.getCount(),
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}

/**
 * Hook to fetch snapshots with pagination
 *
 * @param page - Page number (1-based)
 * @param pageSize - Number of items per page
 * @example
 * const { data, isLoading, error } = useSnapshots(1, 25);
 */
export function useSnapshots(page: number = 1, pageSize: number = 25) {
  console.log('[useSnapshots] Hook called - Page:', page, 'PageSize:', pageSize);
  return useQuery({
    queryKey: [...snapshotKeys.list(), page, pageSize],
    queryFn: async () => {
      console.log('[useSnapshots] queryFn executing...');
      const result = await snapshotService.list(page, pageSize);
      console.log('[useSnapshots] queryFn completed, result:', result);
      return result;
    },
  });
}

/**
 * Hook to fetch all snapshots (for dropdowns)
 * Uses a reasonable page size that the API accepts
 *
 * @example
 * const { data, isLoading, error } = useAllSnapshots();
 */
export function useAllSnapshots() {
  return useQuery({
    queryKey: [...snapshotKeys.all, 'all'],
    queryFn: () => snapshotService.list(1, 100), // Use max page size of 100
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}

/**
 * Hook to fetch a single snapshot by ID
 *
 * @param id - The snapshot ID
 * @example
 * const { data, isLoading, error } = useSnapshot('my-snapshot-id');
 */
export function useSnapshot(id: string) {
  return useQuery({
    queryKey: snapshotKeys.detail(id),
    queryFn: () => snapshotService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to delete a snapshot
 *
 * @example
 * const deleteMutation = useDeleteSnapshot();
 * deleteMutation.mutate('snapshot-id');
 */
export function useDeleteSnapshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => snapshotService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: snapshotKeys.all });
    },
  });
}
